import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MobileTabNav } from '../components/ui/mobile-tab-nav';
import {
  Megaphone, Search, ShoppingCart, Sparkles, TrendingUp, Globe, Printer,
  Clock, CheckCircle, Loader2, Lightbulb, BarChart3, Target, Zap,
  CreditCard, Calendar, Package, ArrowRight, Star, RefreshCw, Tag, Bot
} from 'lucide-react';
import { ReviewSection, InvoiceUploader } from '../components/ReviewsAndInvoices';
import AiChat from '../components/AiChat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIOD_LABELS = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  half_yearly: 'Half Yearly',
  yearly: 'Yearly',
  one_time: 'One Time'
};

const safeDate = (d) => {
  if (!d) return null;
  const s = String(d);
  // If already has timezone info (+00:00 or Z), use as-is; otherwise append Z
  if (s.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(s)) return new Date(s);
  return new Date(s + 'Z');
};
const fmtDate = (d) => { const dt = safeDate(d); return dt && !isNaN(dt) ? dt.toLocaleDateString('en-IN') : '—'; };

const CATEGORY_ICONS = {
  seo: Globe,
  social_media: Megaphone,
  content: Sparkles,
  banner: Printer,
  flyer: Printer,
  branding: Star,
  ppc: Target,
  web_design: Globe,
  default: Package
};

export default function DoctorMarketingPage() {
  const [activeTab, setActiveTab] = useState('browse');
  const [services, setServices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [orders, setOrders] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [walletBalance, setWalletBalance] = useState(0);

  // Order dialog
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [orderNotes, setOrderNotes] = useState('');
  const [placing, setPlacing] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [showReviewService, setShowReviewService] = useState(null);

  const token = localStorage.getItem('clinic_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [servicesRes, ordersRes, walletRes, couponsRes] = await Promise.all([
        axios.get(`${API_URL}/api/b2b/marketing-services/browse`, { headers }),
        axios.get(`${API_URL}/api/b2b/marketing-services/doctor-orders`, { headers }),
        axios.get(`${API_URL}/api/wallet/balance`, { headers }).catch(() => ({ data: { balance: 0 } })),
        axios.get(`${API_URL}/api/b2b/coupons/all-active`, { headers }).catch(() => ({ data: [] }))
      ]);
      setServices(servicesRes.data.services || []);
      setVendors(servicesRes.data.vendors || []);
      setOrders(ordersRes.data || []);
      setWalletBalance(walletRes.data.balance || 0);
      // Filter coupons for marketing vendors
      const allCoupons = Array.isArray(couponsRes.data) ? couponsRes.data : [];
      setAvailableCoupons(allCoupons.filter(c => c.vendor_type === 'marketing'));
    } catch (err) {
      if (err.response?.status !== 401) toast.error('Failed to load marketing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchAISuggestions = async () => {
    setSuggestionsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/b2b/marketing-services/ai-suggestions`, { headers });
      setSuggestions(res.data.suggestions || []);
    } catch {
      toast.error('Failed to load AI suggestions');
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!selectedService) return;
    setPlacing(true);
    try {
      const period = selectedService.is_subscription ? selectedPeriod : 'one_time';
      const res = await axios.post(
        `${API_URL}/api/b2b/marketing-services/subscribe-order`,
        null,
        {
          headers,
          params: {
            vendor_id: selectedService.vendor_id,
            service_id: selectedService.id,
            subscription_period: period,
            notes: orderNotes,
            coupon_code: couponCode
          }
        }
      );
      toast.success('Order placed successfully!');
      setWalletBalance(res.data.wallet_balance);
      setShowOrderDialog(false);
      setSelectedService(null);
      setOrderNotes('');
      setCouponCode('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Order failed');
    } finally {
      setPlacing(false);
    }
  };

  const getPrice = (service, period) => {
    if (!service) return 0;
    if (!service.is_subscription || !service.subscription_pricing) return service.b2b_price || 0;
    return service.subscription_pricing[period] || service.b2b_price || 0;
  };

  const handleCancelMarketingOrder = async (orderId) => {
    if (!window.confirm('Cancel this order? The amount will be refunded to your wallet.')) return;
    try {
      const res = await axios.delete(`${API_URL}/api/b2b/marketing-services/doctor-orders/${orderId}`, { headers });
      toast.success(res.data?.message || 'Order cancelled');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to cancel'); }
  };

  const [editingOrder, setEditingOrder] = useState(null);
  const [editNotes, setEditNotes] = useState('');

  const handleEditOrder = async () => {
    if (!editingOrder) return;
    try {
      await axios.put(`${API_URL}/api/b2b/marketing-services/doctor-orders/${editingOrder.id}`, null, {
        headers, params: { notes: editNotes }
      });
      toast.success('Order updated');
      setEditingOrder(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const filteredServices = services.filter(s => {
    if (searchQuery && !s.name.toLowerCase().includes(searchQuery.toLowerCase()) && !s.description?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (categoryFilter !== 'all' && s.category !== categoryFilter) return false;
    if (typeFilter !== 'all' && s.service_type !== typeFilter) return false;
    return true;
  });

  const categories = [...new Set(services.map(s => s.category).filter(Boolean))];
  const activeSubscriptions = orders.filter(o => o.status !== 'cancelled' && o.expires_at && safeDate(o.expires_at) > new Date());

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="marketing-loading">
      <div className="w-6 h-6 border-2 border-teal-700 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="doctor-marketing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Marketing & Growth
          </h1>
          <p className="text-slate-500 text-sm mt-1">Grow your practice with marketing services from verified vendors</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-sm px-3 py-1.5" data-testid="marketing-wallet-badge">
            <CreditCard className="w-3.5 h-3.5 mr-1.5" /> ₹{walletBalance.toLocaleString()}
          </Badge>
          <Badge variant="secondary" className="text-sm px-3 py-1.5">
            <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> {activeSubscriptions.length} Active
          </Badge>
        </div>
      </div>

      {/* Tab Navigation */}
      <MobileTabNav
        tabs={[
          { id: 'browse', label: 'Browse Services', icon: Search },
          { id: 'orders', label: 'My Orders', icon: ShoppingCart, badge: activeSubscriptions.length || null },
          { id: 'ai-insights', label: 'AI Insights', icon: Sparkles, onClick: () => { if (!suggestions.length) fetchAISuggestions(); } },
          { id: 'ai-chat', label: '1Clin AI', icon: Bot },
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="hidden">
          <TabsTrigger value="browse">Browse</TabsTrigger>
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="ai-insights">AI</TabsTrigger>
          <TabsTrigger value="ai-chat">1Clin AI</TabsTrigger>
        </TabsList>

        {/* Browse Tab */}
        <TabsContent value="browse">
          {/* Search & Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="marketing-search-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="marketing-category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-[140px]" data-testid="marketing-type-filter">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filteredServices.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Megaphone className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500 font-medium">No marketing services available</p>
                <p className="text-slate-400 text-sm mt-1">Link with marketing vendors from your B2B Marketplace to see their services here.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredServices.map(service => {
                const IconComp = CATEGORY_ICONS[service.category] || CATEGORY_ICONS.default;
                return (
                  <Card key={service.id} className="hover:shadow-md transition-shadow border-slate-200" data-testid={`marketing-service-card-${service.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center">
                          <IconComp className="w-5 h-5 text-violet-600" />
                        </div>
                        <div className="flex gap-1.5">
                          <Badge variant={service.service_type === 'online' ? 'default' : 'secondary'} className="text-[10px]">
                            {service.service_type === 'online' ? 'Online' : 'Offline'}
                          </Badge>
                          {service.is_subscription && (
                            <Badge variant="outline" className="text-[10px] border-violet-300 text-violet-600">Subscription</Badge>
                          )}
                        </div>
                      </div>
                      <h3 className="font-semibold text-slate-800 mb-1">{service.name}</h3>
                      <p className="text-xs text-slate-500 mb-2 line-clamp-2">{service.description || 'Marketing service'}</p>
                      <p className="text-[10px] text-slate-400 mb-3">by {service.vendor_name}</p>

                      {service.is_subscription && service.subscription_pricing ? (
                        <div className="grid grid-cols-2 gap-1 mb-3">
                          {Object.entries(service.subscription_pricing).filter(([, v]) => v).map(([period, price]) => (
                            <div key={period} className="text-center bg-slate-50 rounded px-2 py-1">
                              <p className="text-[10px] text-slate-400">{PERIOD_LABELS[period]}</p>
                              <p className="text-sm font-bold text-slate-800">₹{price.toLocaleString()}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-lg font-bold text-slate-900 mb-3">₹{(service.b2b_price || 0).toLocaleString()}</p>
                      )}

                      {service.turnaround_time && (
                        <p className="text-[10px] text-slate-400 mb-3 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {service.turnaround_time}
                        </p>
                      )}

                      <Button
                        size="sm"
                        className="w-full bg-violet-600 hover:bg-violet-700"
                        onClick={() => {
                          setSelectedService(service);
                          setSelectedPeriod(service.is_subscription ? 'monthly' : 'one_time');
                          setShowOrderDialog(true);
                        }}
                        data-testid={`order-service-${service.id}`}
                      >
                        <ShoppingCart className="w-3.5 h-3.5 mr-1.5" />
                        {service.is_subscription ? 'Subscribe' : 'Place Order'}
                      </Button>
                      {/* Reviews */}
                      <div className="mt-3 pt-3 border-t">
                        <ReviewSection productId={service.id} vendorId={service.vendor_id} productType="marketing_service" canReview={true} token={token} />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders">
          {orders.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">No marketing orders yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {orders.map(order => (
                <Card key={order.id} data-testid={`marketing-order-${order.id}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-800">{order.service_name}</h3>
                          <Badge variant={order.status === 'confirmed' || order.status === 'completed' ? 'default' : order.status === 'processing' ? 'secondary' : 'destructive'} className="text-[10px]">
                            {order.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500">{order.vendor_name} | {order.order_number}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> {fmtDate(order.created_at)}
                          </span>
                          {order.subscription_period !== 'one_time' && (
                            <span className="flex items-center gap-1">
                              <RefreshCw className="w-3 h-3" /> {PERIOD_LABELS[order.subscription_period]}
                            </span>
                          )}
                          {order.expires_at && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" /> Expires: {fmtDate(order.expires_at)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-slate-900">₹{order.price?.toLocaleString()}</p>
                        {order.coupon_code && <p className="text-[10px] text-violet-600">Coupon: {order.coupon_code} (-₹{order.coupon_discount})</p>}
                      </div>
                    </div>
                    <InvoiceUploader orderId={order.id} orderType="marketing" canUpload={false} />
                    {!['completed', 'cancelled'].includes(order.status) && (
                      <div className="flex gap-2 mt-3 pt-2 border-t">
                        <Button variant="outline" size="sm" onClick={() => { setEditingOrder(order); setEditNotes(order.notes || ''); }} data-testid={`edit-mkt-order-${order.id}`}>
                          <Zap className="w-3 h-3 mr-1" /> Edit
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleCancelMarketingOrder(order.id)} data-testid={`cancel-mkt-order-${order.id}`}>
                          Cancel
                        </Button>
                      </div>
                    )}
                    {order.status === 'cancelled' && <p className="text-xs text-red-500 mt-2">Cancelled — Refund processed</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="ai-insights">
          <Card className="mb-4">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-violet-500" /> AI Marketing Advisor
                  </CardTitle>
                  <CardDescription>Personalized recommendations to grow your practice</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchAISuggestions} disabled={suggestionsLoading} data-testid="refresh-ai-suggestions">
                  {suggestionsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {suggestionsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-violet-500 mr-3" />
                  <p className="text-slate-500">Generating personalized suggestions...</p>
                </div>
              ) : suggestions.length === 0 ? (
                <div className="text-center py-12">
                  <Lightbulb className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500">Click refresh to get AI-powered marketing suggestions</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {suggestions.map((s, idx) => (
                    <div key={idx} className="p-4 bg-gradient-to-r from-slate-50 to-violet-50/30 rounded-xl border border-slate-100" data-testid={`ai-suggestion-${idx}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px]">{s.category}</Badge>
                          <h4 className="font-semibold text-slate-800">{s.title}</h4>
                        </div>
                        <Badge variant={s.estimated_impact === 'high' ? 'default' : s.estimated_impact === 'medium' ? 'secondary' : 'outline'} className="text-[10px]">
                          {s.estimated_impact} impact
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 mb-2">{s.description}</p>
                      {s.estimated_cost_range && (
                        <p className="text-xs text-slate-400 flex items-center gap-1">
                          <CreditCard className="w-3 h-3" /> Est. Cost: {s.estimated_cost_range}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 1Clin AI Chat Tab */}
        <TabsContent value="ai-chat">
          <AiChat token={token} />
        </TabsContent>
      </Tabs>

      {/* Order/Subscribe Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={(v) => { if (!placing) setShowOrderDialog(v); }}>
        <DialogContent className="max-w-md" data-testid="marketing-order-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {selectedService?.is_subscription ? 'Subscribe to Service' : 'Place Order'}
            </DialogTitle>
          </DialogHeader>
          {selectedService && (
            <div className="space-y-4">
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                <h3 className="font-semibold text-slate-800">{selectedService.name}</h3>
                <p className="text-xs text-slate-500 mt-1">{selectedService.vendor_name}</p>
                {selectedService.deliverables && (
                  <p className="text-xs text-slate-600 mt-2">Deliverables: {selectedService.deliverables}</p>
                )}
              </div>

              {selectedService.is_subscription && selectedService.subscription_pricing && (
                <div className="space-y-2">
                  <Label>Subscription Period</Label>
                  <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                    <SelectTrigger data-testid="subscription-period-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(selectedService.subscription_pricing).filter(([, v]) => v).map(([period, price]) => (
                        <SelectItem key={period} value={period}>
                          {PERIOD_LABELS[period]} — ₹{price.toLocaleString()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Input
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  placeholder="Any specific requirements..."
                  data-testid="marketing-order-notes"
                />
              </div>

              {/* Coupon Code */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Tag className="w-3 h-3" /> Coupon Code</Label>
                <div className="flex gap-2">
                  <Input
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter coupon code"
                    className="flex-1"
                    data-testid="marketing-coupon-input"
                  />
                </div>
                {availableCoupons.filter(c => c.vendor_id === selectedService?.vendor_id).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {availableCoupons.filter(c => c.vendor_id === selectedService?.vendor_id).map(c => (
                      <button key={c.id} type="button"
                        onClick={() => setCouponCode(c.code)}
                        className={`text-[10px] px-2 py-0.5 rounded-full border ${couponCode === c.code ? 'bg-violet-100 border-violet-400 text-violet-700' : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'}`}>
                        {c.code} ({c.discount_type === 'percentage' ? `${c.discount_value}%` : `₹${c.discount_value}`} off)
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-slate-50 rounded-lg p-3 space-y-1.5 text-sm">
                {(() => {
                  const basePrice = getPrice(selectedService, selectedPeriod);
                  const matchedCoupon = couponCode ? availableCoupons.find(c => c.code === couponCode.toUpperCase() && c.vendor_id === selectedService?.vendor_id) : null;
                  let discount = 0;
                  if (matchedCoupon) {
                    if (matchedCoupon.applicable_tiers?.length && !matchedCoupon.applicable_tiers.includes(selectedService?.is_subscription ? selectedPeriod : 'one_time')) discount = 0;
                    else if (matchedCoupon.discount_type === 'percentage') discount = Math.round(basePrice * matchedCoupon.discount_value / 100);
                    else discount = Math.min(matchedCoupon.discount_value, basePrice);
                  }
                  const finalPrice = Math.max(0, basePrice - discount);
                  return (
                    <>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Service Cost</span>
                        <span className="font-medium">₹{basePrice.toLocaleString()}</span>
                      </div>
                      {discount > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Coupon ({couponCode})</span>
                          <span className="font-medium">-₹{discount.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-slate-500">Wallet Balance</span>
                        <span className="font-medium">₹{walletBalance.toLocaleString()}</span>
                      </div>
                      <div className="border-t pt-1.5 flex justify-between font-bold">
                        <span>After Deduction</span>
                        <span className={walletBalance >= finalPrice ? 'text-emerald-700' : 'text-red-600'}>
                          ₹{(walletBalance - finalPrice).toLocaleString()}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowOrderDialog(false)} disabled={placing}>Cancel</Button>
            <Button
              onClick={handlePlaceOrder}
              disabled={placing || walletBalance < getPrice(selectedService, selectedPeriod)}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="confirm-marketing-order-btn"
            >
              {placing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              {walletBalance < getPrice(selectedService, selectedPeriod) ? 'Insufficient Balance' : 'Confirm & Pay'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Order Dialog */}
      <Dialog open={!!editingOrder} onOpenChange={(open) => { if (!open) setEditingOrder(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Order Notes</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">{editingOrder?.order_number} — {editingOrder?.service_name}</p>
            <div>
              <Label>Notes / Instructions</Label>
              <Input value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Special instructions..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingOrder(null)}>Cancel</Button>
            <Button onClick={handleEditOrder} data-testid="save-edit-mkt-order">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
